import { Component } from '@theme/component';

/**
 * A countdown timer that ticks down to a target date/time, freezing at all zeros once
 * the target passes.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} days
 * @property {HTMLElement} hours
 * @property {HTMLElement} minutes
 * @property {HTMLElement} seconds
 *
 * @extends {Component<Refs>}
 */
export class CountdownTimer extends Component {
  /**
   * The interval ID for the per-second tick.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * The target timestamp in milliseconds, or NaN if the attribute is invalid.
   * @type {number}
   */
  #target = NaN;

  connectedCallback() {
    super.connectedCallback();

    this.#target = this.#parseEndTime(this.getAttribute('end-time'));
    if (Number.isNaN(this.#target)) return;

    this.#tick();
    this.#interval = setInterval(() => this.#tick(), 1000);
  }

  /**
   * Parses the `end-time` attribute into a timestamp (ms) in the visitor's local timezone.
   *
   * Built explicitly via the numeric Date constructor rather than `new Date(string)` because
   * Safari's date string parser is strict: it returns Invalid Date for ISO strings whose
   * components aren't zero-padded (e.g. `2026-1-5T9:5:0`) or that end with a bare `T`.
   *
   * @param {string | null} value
   * @returns {number} The target timestamp, or NaN if it can't be parsed.
   */
  #parseEndTime(value) {
    if (!value) return NaN;

    const match = value
      .trim()
      .match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T\s](\d{1,2}):(\d{1,2})(?::(\d{1,2}))?)?/);

    if (match) {
      const [, year, month, day, hours = '0', minutes = '0', seconds = '0'] = match;
      return new Date(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hours),
        Number(minutes),
        Number(seconds)
      ).getTime();
    }

    // Fall back to native parsing for any other format.
    return new Date(value).getTime();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stop();
  }

  #stop() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  #tick() {
    // Avoid recomputing while the tab is hidden; the next visible tick catches up.
    if (document.hidden) return;

    const diff = Math.max(0, this.#target - Date.now());

    const days = Math.floor(diff / 86_400_000);
    const hours = Math.floor((diff % 86_400_000) / 3_600_000);
    const minutes = Math.floor((diff % 3_600_000) / 60_000);
    const seconds = Math.floor((diff % 60_000) / 1000);

    this.refs.days.textContent = this.#pad(days);
    this.refs.hours.textContent = this.#pad(hours);
    this.refs.minutes.textContent = this.#pad(minutes);
    this.refs.seconds.textContent = this.#pad(seconds);

    if (diff <= 0) this.#stop();
  }

  /**
   * Zero-pads a value to at least two digits.
   * @param {number} value
   * @returns {string}
   */
  #pad(value) {
    return `${value}`.padStart(2, '0');
  }
}

if (!customElements.get('countdown-timer-component')) {
  customElements.define('countdown-timer-component', CountdownTimer);
}
